import {
  arrivalPdn,
  arrivalScapture,
  arrivalStockTransfer,
  arrivalTrading,
} from "./arrival";
import { weighbridgeViolations } from "../arrival";
import {
  arrivalCob,
  arrivalCobReceipt,
  cobBlend,
  cobDispatch,
  cobDrying,
} from "./cob";
import {
  dispatchBulk,
  dispatchDiscard,
  dispatchPack,
  dispatchStockTransfer,
  dispatchTdf,
} from "./dispatch";
import {
  dryingSlip,
  lotBlending,
  packingSlip,
  packUnpacking,
  ivt,
  fcPackingSlip,
  oflPackingSlip,
  packRevalidate,
  packingWorkOrder,
  packP2c,
  processingPackingSlip,
  processingPackingFcSlip,
  reprintWorkOrder,
  rootStockNosCount,
  processingSlip,
  slocUpdation,
  thermalTreatment,
  primingTreatment,
  colorSortSlip,
  treatmentSlip,
} from "./processing";
import {
  DELIVERY_TYPE_LABEL,
  HEADER_EXTRA_KEYS,
  HEADER_FIELD_META,
  HEADER_TRANSPORT_KEYS,
  HEADER_WEIGHBRIDGE_KEYS,
  LINE_COLUMN_META,
} from "./fields";
import type {
  DetailField,
  LineColumnGroup,
  DetailSection,
  FormConfig,
  HeaderFieldKey,
  TransportConfig,
  LineColumnKey,
  TransportFieldKey,
  TransportMode,
} from "./types";

export * from "./types";
export * from "./fields";
export * from "./arrival";
export * from "./cob";
export * from "./variety-label";
export * from "./dispatch";
export * from "./processing";
export * from "./sathi-portal";

export const FORMS: Record<string, FormConfig> = Object.fromEntries(
  [
    arrivalPdn,
    arrivalScapture,
    arrivalTrading,
    arrivalStockTransfer,
    arrivalCob,
    cobBlend,
    cobDrying,
    cobDispatch,
    arrivalCobReceipt,
    dispatchStockTransfer,
    dispatchPack,
    dispatchBulk,
    dispatchTdf,
    dispatchDiscard,
    slocUpdation,
    dryingSlip,
    processingSlip,
    processingPackingSlip,
    processingPackingFcSlip,
    lotBlending,
    packingWorkOrder,
    reprintWorkOrder,
    rootStockNosCount,
    packP2c,
    ivt,
    oflPackingSlip,
    packRevalidate,
    fcPackingSlip,
    thermalTreatment,
    primingTreatment,
    colorSortSlip,
    treatmentSlip,
    packingSlip,
    packUnpacking,
  ].map((f) => [f.code, f])
);

/**
 * Forms that own a URL and a nav entry. A chain STAGE is reached only through its head, and is
 * kept out of the combined document list so one truck reads as one row rather than five.
 */
export const listableForms = (): FormConfig[] =>
  Object.values(FORMS).filter((f) => f.chainRole !== "stage");

/** Forms whose arrivals stay off the handheld until the desk finalizes their setup. */
export const deskSetupFormCodes = (): string[] =>
  Object.values(FORMS)
    .filter((f) => f.setupOnDesk === true)
    .map((f) => f.code);

/** Whether the handheld may see this arrival yet: not while the desk is still setting it up. */
export function onHandheldYet(a: {
  formCode: string;
  setupStage: string | null;
}): boolean {
  return !(
    FORMS[a.formCode]?.setupOnDesk === true && a.setupStage !== "complete"
  );
}

export function formConfig(code: string): FormConfig {
  const f = FORMS[code];
  if (!f) throw new Error(`unknown form ${code}`);
  return f;
}

export type WeighingMode = "bags" | "lot";

/** A receipt's answer to "weighed bag by bag?", read off `documents.extra`; null when never asked. */
export function weighingModeOf(extra: unknown): WeighingMode | null {
  const m = (extra as { weighingMode?: unknown } | null)?.weighingMode;
  return m === "bags" || m === "lot" ? m : null;
}

/**
 * The form as THIS document runs it. A Stock Transfer In typed per lot is not bag-wise and has no
 * desk check; every other document runs its form unchanged. Everything that gates on `bagWise` or
 * `checkStep` for a saved document reads the form through here, so a lot-entry receipt takes the
 * typed-totals path the desk-raised forms already use, with no second rule set.
 */
export function formFor(doc: {
  formCode: string;
  extra?: unknown;
}): FormConfig {
  const f = formConfig(doc.formCode);
  return f.weighingChoice && weighingModeOf(doc.extra) === "lot"
    ? {
        ...f,
        bagWise: false,
        checkStep: false,
        // No bag was weighed, so there is no Unloading Sheet to print (nor an RSUS No. to spend).
        documents: f.documents.filter((k) => k !== "unloading_sheet"),
      }
    : f;
}

/** Takes only the transport block, so the browser's narrower form DTO can ask too. */
export function transportFieldEnabled(
  form: { transport: TransportConfig },
  key: TransportFieldKey
): boolean {
  return (
    form.transport.enabled &&
    (form.transport.fields.length === 0 || form.transport.fields.includes(key))
  );
}

export interface TransportValues {
  mode?: TransportMode | null | undefined;
  transportName?: string | null | undefined;
  lorryReceiptNumber?: string | null | undefined;
  vehicleNumber?: string | null | undefined;
  paymentMode?: string | null | undefined;
  /** HEADER_TRANSPORT_KEYS maps `dispatch_date` here, so the type has to carry it. */
  dispatchDate?: string | null | undefined;
  dcNumber?: string | null | undefined;
  dcDate?: string | null | undefined;
  courierName?: string | null | undefined;
  docketNo?: string | null | undefined;
  deliveryPersonName?: string | null | undefined;
  /**
   * The plate the gate recorded for this truck, stamped by the server when the arrival is tied to
   * its gate record (S-Capture gate entry, Gate Pass In, Stock Transfer / cob inward). Set, the
   * Delivery Type is Vehicle and Vehicle No. is this plate, locked: neither screen may change it.
   * Never written by a client (`updateHeader` strips it).
   */
  gateVehicleNumber?: string | null | undefined;
  /**
   * Stamped by the server when the arrival is tied to a gate record that carried no LR: LR No. is
   * then "N/A" and locked on both screens (user, 2026-10-06). Never written by a client.
   */
  gateLrNotGiven?: boolean | null | undefined;
}

/** The gate's plate when the arrival's vehicle is locked to it, else null. */
export function gateVehicleOf(
  t: TransportValues | null | undefined
): string | null {
  const v = t?.gateVehicleNumber;
  return typeof v === "string" && v.trim() ? v.trim() : null;
}

/** True when the gate gave no LR, so the arrival's LR No. is "N/A" and locked. */
export function gateLrLockedOf(t: TransportValues | null | undefined): boolean {
  return t?.gateLrNotGiven === true;
}

/**
 * Paperwork an arrival cannot be completed without, as operator-facing labels, branched by mode
 * exactly as vnr-prod's finalize validator: Transport wants name/vehicle/LR plus a gate pass,
 * Courier wants courier/docket/gate pass, Hand delivery only the delivery person.
 */
export function missingTransportFields(
  /** Narrowed like `transportFieldEnabled`, so the browser's form DTO can ask too. */
  form: { transport: TransportConfig; dcInfo: boolean },
  t: TransportValues
): string[] {
  return missingTransportBlockers(form, t).map((b) => b.label);
}

/** A mandatory field left blank: its key (for the page to jump to) and its label (to name it). */
export interface MissingField<K extends string = string> {
  key: K;
  label: string;
}

/**
 * `missingTransportFields` with the field each gap is in, in screen order: `mode` is the Mode of
 * Transit choice, the rest are `TransportFieldKey`s.
 */
export function missingTransportBlockers(
  form: { transport: TransportConfig; dcInfo: boolean },
  t: TransportValues
): MissingField<TransportFieldKey | "mode">[] {
  const blank = (v: string | null | undefined) => !(v ?? "").trim();
  const collects = (k: TransportFieldKey) => transportFieldEnabled(form, k);
  const missing: MissingField<TransportFieldKey | "mode">[] = [];
  const need = (
    key: TransportFieldKey,
    label: string,
    v: string | null | undefined
  ) => {
    if (collects(key) && blank(v)) missing.push({ key, label });
  };
  if (form.transport.enabled) {
    if (!t.mode) missing.push({ key: "mode", label: DELIVERY_TYPE_LABEL });
    else if (t.mode === "transport") {
      need("transport_name", "Transport Name", t.transportName);
      need("vehicle_number", "Vehicle Number", t.vehicleNumber);
      need("lorry_receipt_number", "LR Number", t.lorryReceiptNumber);
      need("payment_mode", "Payment Mode", t.paymentMode);
    } else if (t.mode === "courier") {
      need("courier_name", "Courier Name", t.courierName);
      need("docket_no", "Docket Number", t.docketNo);
    } else if (t.mode === "hand_delivery") {
      need(
        "delivery_person_name",
        "Delivery Person Name",
        t.deliveryPersonName
      );
    }
  }
  if (form.dcInfo && blank(t.dcNumber))
    missing.push({ key: "dc_number", label: "DC Number" });
  return missing;
}

/** What the floor's hand-over writes into a required transport field the handheld left blank. */
export const TRANSPORT_NOT_GIVEN = "N/A";

/**
 * The floor's transport, with every field `missingTransportFields` would ask for filled with "N/A",
 * except Payment Mode, which has no "N/A" (user, 2026-10-18) and is left for the desk to pick.
 *
 * The handheld does not ask for everything the desk's Submit wants (LR on a truck that started as
 * Hand Delivery and was switched later), and once the floor previews, the desk can no longer edit
 * transport. So the hand-over fills the blanks (user, 2026-09-30: "if not filled from mobile fill
 * the fields with N/A") rather than leaving a truck nobody can submit.
 * Anything typed is kept; `filled` names what was written, for the audit remark.
 */
export function fillBlankTransport<T extends TransportValues>(
  form: { transport: TransportConfig; dcInfo: boolean },
  t: T
): { transport: T; filled: string[] } {
  const blank = (v: string | null | undefined) => !(v ?? "").trim();
  const collects = (k: TransportFieldKey) => transportFieldEnabled(form, k);
  const out: TransportValues = { ...t };
  const filled: string[] = [];
  const fill = (
    key: TransportFieldKey,
    field: Exclude<keyof TransportValues, "gateLrNotGiven">,
    label: string
  ) => {
    if (!collects(key) || !blank(out[field])) return;
    (out as Record<string, unknown>)[field] = TRANSPORT_NOT_GIVEN;
    filled.push(label);
  };
  if (form.transport.enabled) {
    if (!out.mode) {
      out.mode = "transport";
      filled.push(DELIVERY_TYPE_LABEL);
    }
    if (out.mode === "transport") {
      fill("transport_name", "transportName", "Transport Name");
      fill("vehicle_number", "vehicleNumber", "Vehicle Number");
      fill("lorry_receipt_number", "lorryReceiptNumber", "LR Number");
      // Never Payment Mode (user, 2026-10-18: no "N/A" mode): the desk picks a blank one while
      // previewed (`updateHeader`), and Submit asks for it.
    } else if (out.mode === "courier") {
      fill("courier_name", "courierName", "Courier Name");
      fill("docket_no", "docketNo", "Docket Number");
    } else if (out.mode === "hand_delivery")
      fill(
        "delivery_person_name",
        "deliveryPersonName",
        "Delivery Person Name"
      );
  }
  if (form.dcInfo && blank(out.dcNumber)) {
    out.dcNumber = TRANSPORT_NOT_GIVEN;
    filled.push("DC Number");
  }
  return { transport: out as T, filled };
}

/**
 * The post item form a form gets when it has not declared one.
 *
 * seedtrac gives each arrival type its own near-identical add page; we keep one screen and vary the
 * declaration. Deriving a sensible default means the forms whose screenshots we have not yet
 * verified get a usable panel instead of an empty one, and only `arrival_pdn` — the one we have
 * verified — pins its layout explicitly.
 */
export function defaultDetailSections(form: FormConfig): DetailSection[] {
  const cols = form.lineColumns.filter((c) => c !== "actions");
  const inGroup = new Set<LineColumnKey>();
  const matrixColumns: string[] = [];
  const matrixCells: LineColumnKey[][] = [];
  /** A group that also carries Tare Weight (Trading's Actual) shows it under its Quantity. */
  let tareColumn: number | null = null;

  // The declared/received/difference spans the form already declares become the matrix, so a
  // transfer reads "Dispatch | Received | Difference" and trading "As Per DC | Actual | Difference"
  // without either having to say so twice.
  for (const g of form.lineGroups ?? []) {
    const qty = g.columns.find(
      (c) => !LINE_COLUMN_META[c].label.includes("NoB")
    );
    const bags = g.columns.find((c) =>
      LINE_COLUMN_META[c].label.includes("NoB")
    );
    if (!qty || !bags) continue;
    if (g.columns.includes("tare_weight")) tareColumn = matrixColumns.length;
    matrixColumns.push(g.label);
    matrixCells.push([bags, qty]);
    for (const c of g.columns) inGroup.add(c);
  }

  const rest = cols.filter((c) => !inGroup.has(c) && c !== "storage");
  const readonly = rest.filter((c) => !LINE_COLUMN_META[c].editable);
  const editable = rest.filter((c) => LINE_COLUMN_META[c].editable);

  // The form's own column wording and its mandatory keys carry into the panel.
  const required = new Set(form.requiredLineFields ?? []);
  const field = (key: LineColumnKey): DetailField => {
    const label = form.columnLabels?.[key];
    return {
      key,
      ...(label ? { label } : {}),
      ...(required.has(key) ? { required: true } : {}),
    };
  };

  const blocks: DetailSection["blocks"] = [];
  if (readonly.length)
    blocks.push({ kind: "fields", fields: readonly.map(field) });
  if (matrixColumns.length)
    blocks.push({
      kind: "matrix",
      columns: matrixColumns,
      rows: [
        { label: "No. of Bags (NoB)", cells: matrixCells.map((c) => c[0]!) },
        {
          label: "Quantity",
          cells: matrixCells.map((c) => c[1]!),
          ...(tareColumn !== null
            ? { extra: { key: "tare_weight", column: tareColumn } }
            : {}),
        },
      ],
    });
  if (editable.length)
    blocks.push({ kind: "fields", fields: editable.map(field) });

  const sections: DetailSection[] = blocks.length ? [{ blocks }] : [];
  if (cols.includes("storage"))
    sections.push({
      title: "Storage Location (SLOC)",
      blocks: [{ kind: "sloc" }],
    });
  return sections;
}

/**
 * What the browser is allowed to know about a form — the API's shape for `doc.form`.
 *
 * It lives here, not in the API, because the web builds one too: the New Arrival screen renders
 * before any document exists, so it has to derive the same projection from `FORMS` locally. Two
 * copies of these defaults would drift, and the screen before the first lot would quietly differ
 * from the screen after it.
 */
export const publicForm = (f: FormConfig) => ({
  code: f.code,
  title: f.title,
  declaredLabel: f.declaredLabel,
  lotSource: f.lotSource,
  feedPrefill: f.feedPrefill ?? null,
  lotgenScope: f.lotgenScope ?? null,
  typedLotIdentifier: f.typedLotIdentifier ?? false,
  raisedOnHandheld: f.raisedOnHandheld ?? false,
  // The desk shows Mark Checked / Send back for reweigh, and holds SLOC until the sheet is checked.
  checkStep: f.checkStep ?? false,
  // The handheld weighs a bag only from a scan of its barcode (S-Capture).
  bagBarcodeRequired: f.bagBarcodeRequired ?? false,
  // A receipt: its lots are the dispatch's. The browser hides lot entry and Delete on one.
  receivesFrom: f.receivesFrom ?? null,
  declaredTypedAtSetup: f.declaredTypedAtSetup ?? false,
  allowManualLots: f.allowManualLots,
  blindCoded: f.blindCoded,
  // The browser needs this to know the Actual pair is weighed, not typed: on a bag-wise form the
  // Post Item Form renders those cells read-only. Without it the desk gets an input that the API
  // refuses, which is an error toast rather than an explanation.
  bagWise: f.bagWise,
  weighingChoice: f.weighingChoice ?? false,
  transport: f.transport,
  dcInfo: f.dcInfo,
  lineColumns: f.lineColumns,
  lineGroups: f.lineGroups ?? [],
  headerFields: f.headerFields,
  requiredHeaderFields: f.requiredHeaderFields ?? [],
  lockedHeaderFields: f.lockedHeaderFields ?? [],
  previewEditableHeaderFields: f.previewEditableHeaderFields ?? [],
  headerLabels: f.headerLabels ?? {},
  columnLabels: f.columnLabels ?? {},
  requiredLineFields: f.requiredLineFields ?? [],
  slocOptional: f.slocOptional ?? false,
  recoveryFromWeighbridge: f.recoveryFromWeighbridge ?? false,
  headerDefaults: f.headerDefaults ?? {},
  lotEntry: f.lotEntry ?? null,
  // Resolved here, so the browser never has to branch on whether a form declared its own panel.
  detailSections: f.detailSections ?? defaultDetailSections(f),
  defaultLeMonths: f.defaultLeMonths ?? null,
  documents: f.documents,
  destKind: f.destKind,
  // Projected, not derived on the client: `draftShell()` builds this same object in the browser
  // before any document exists, so a field left out here makes the screen before the first lot
  // quietly differ from the screen after it.
  chainRole: f.chainRole ?? null,
  chainStage: f.chainStage ?? null,
  repeatable: f.repeatable ?? false,
  provisional: f.provisional ?? false,
});

/** Every line key a form can capture, on either surface. What a print is allowed to expect. */
export function formLineKeys(form: FormConfig): LineColumnKey[] {
  const keys = new Set<LineColumnKey>(form.lineColumns);
  for (const s of form.detailSections ?? [])
    for (const b of s.blocks) {
      if (b.kind === "fields") for (const f of b.fields) keys.add(f.key);
      else if (b.kind === "matrix")
        for (const r of b.rows) {
          for (const c of r.cells) if (c) keys.add(c);
          if (r.extra) keys.add(r.extra.key);
        }
    }
  return [...keys];
}

export interface WeighbridgeValues {
  loadedKg?: string | number | null | undefined;
  emptyKg?: string | number | null | undefined;
  netKg?: string | number | null | undefined;
  ticketNo?: string | null | undefined;
}

export interface HeaderValues {
  date?: string | null | undefined;
  /** `dest_plant` lands on a column of its own, not in `extra`. */
  destFacilityId?: number | null | undefined;
  transport?: TransportValues | null | undefined;
  extra?: Record<string, unknown> | null | undefined;
  weighbridge?: WeighbridgeValues | null | undefined;
}

/** A header field's label on this form: its own wording when it declares one. */
export const headerLabel = (
  form: { headerLabels?: Partial<Record<HeaderFieldKey, string>> | undefined },
  key: HeaderFieldKey
): string => form.headerLabels?.[key] ?? HEADER_FIELD_META[key].label;

/** A grid column's label on this form: its own wording when it declares one. */
export const columnLabel = (
  form: { columnLabels?: Partial<Record<LineColumnKey, string>> | undefined },
  key: LineColumnKey
): string => form.columnLabels?.[key] ?? LINE_COLUMN_META[key].label;

/**
 * Header fields carrying seedtrac's red asterisk, as operator-facing labels — the header's answer to
 * `missingTransportFields`, checked at the same point in the flow.
 */
export function missingHeaderFields(
  form: {
    requiredHeaderFields?: HeaderFieldKey[] | undefined;
    headerLabels?: Partial<Record<HeaderFieldKey, string>> | undefined;
  },
  doc: HeaderValues
): string[] {
  return missingHeaderBlockers(form, doc).map((b) => b.label);
}

/** `missingHeaderFields` with the field each gap is in. */
export function missingHeaderBlockers(
  /** Narrowed like `transportFieldEnabled`, so the browser's form DTO can ask too. */
  form: {
    requiredHeaderFields?: HeaderFieldKey[] | undefined;
    headerLabels?: Partial<Record<HeaderFieldKey, string>> | undefined;
  },
  doc: HeaderValues
): MissingField<HeaderFieldKey>[] {
  const t = (doc.transport ?? {}) as Record<string, unknown>;
  const extra = doc.extra ?? {};
  const bridge = (doc.weighbridge ?? {}) as Record<string, unknown>;
  const missing: MissingField<HeaderFieldKey>[] = [];
  for (const key of form.requiredHeaderFields ?? []) {
    const transportKey = HEADER_TRANSPORT_KEYS[
      key as keyof typeof HEADER_TRANSPORT_KEYS
    ] as string | undefined;
    const extraKey = HEADER_EXTRA_KEYS[
      key as keyof typeof HEADER_EXTRA_KEYS
    ] as string | undefined;
    const bridgeKey = HEADER_WEIGHBRIDGE_KEYS[
      key as keyof typeof HEADER_WEIGHBRIDGE_KEYS
    ] as string | undefined;
    const value =
      key === "dest_plant"
        ? doc.destFacilityId
        : key === "arrival_date"
          ? doc.date
          : transportKey
            ? t[transportKey]
            : bridgeKey
              ? bridge[bridgeKey]
              : extraKey
                ? extra[extraKey]
                : undefined;
    // A header value is a date, a number or a string; anything else is as good as blank.
    const filled =
      typeof value === "number" ||
      (typeof value === "string" && value.trim() !== "");
    if (!filled) missing.push({ key, label: headerLabel(form, key) });
  }
  return missing;
}

/**
 * Does this form show the weighbridge at all?
 *
 * One predicate, read by the screen that renders the trio AND by the write path that accepts it,
 * so a form can never persist a reading it does not display. `loaded_weight` is the marker because
 * the three are declared together or not at all.
 */
export function collectsWeighbridge(form: {
  headerFields: HeaderFieldKey[];
}): boolean {
  return form.headerFields.includes("loaded_weight");
}

/**
 * What is wrong with this document's weighbridge pair, as operator-facing text — the header's
 * third blocker list, beside `missingTransportFields` and `missingHeaderFields`.
 *
 * Silent on a form that does not collect it, and silent on an unweighed vehicle: the reading is
 * documentation (A3 removed weighbridge apportionment), so a missing ticket never blocks a submit.
 * It speaks only when the two numbers cannot both be true.
 */
export function weighbridgeBlockers(
  form: { headerFields: HeaderFieldKey[] },
  w: WeighbridgeValues | null | undefined
): string[] {
  if (!collectsWeighbridge(form)) return [];
  return weighbridgeViolations(w?.loadedKg, w?.emptyKg);
}

/**
 * Required post-item-form fields a line has not filled, as operator-facing labels.
 *
 * Required-ness is declared once, in `detailSections`, and read here — so the red asterisk, the
 * disabled Post button and any server-side check all come from the same place rather than from a
 * client schema that drifts from the config.
 */
export function missingLotFields(
  form: {
    detailSections?: DetailSection[] | undefined;
    requiredLineFields?: LineColumnKey[] | undefined;
    columnLabels?: Partial<Record<LineColumnKey, string>> | undefined;
    lineGroups?: LineColumnGroup[] | undefined;
  },
  has: (key: LineColumnKey) => boolean
): string[] {
  return missingLotBlockers(form, has).map((b) => b.label);
}

/** `missingLotFields` with the column each gap is in. */
export function missingLotBlockers(
  form: {
    detailSections?: DetailSection[] | undefined;
    requiredLineFields?: LineColumnKey[] | undefined;
    columnLabels?: Partial<Record<LineColumnKey, string>> | undefined;
    lineGroups?: LineColumnGroup[] | undefined;
  },
  has: (key: LineColumnKey) => boolean
): MissingField<LineColumnKey>[] {
  const missing: MissingField<LineColumnKey>[] = [];
  const seen = new Set<LineColumnKey>();
  for (const s of form.detailSections ?? [])
    for (const b of s.blocks) {
      if (b.kind !== "fields") continue;
      for (const f of b.fields) {
        if (!f.required || seen.has(f.key) || has(f.key)) continue;
        seen.add(f.key);
        const meta = LINE_COLUMN_META[f.key];
        missing.push({
          key: f.key,
          label: f.label ?? meta.formLabel ?? meta.label,
        });
      }
    }
  // The form-level list: a derived panel is not always resolved on the caller's config, and a matrix
  // cell (As Per DC) has no field entry to carry `required`.
  for (const key of form.requiredLineFields ?? []) {
    if (seen.has(key) || has(key)) continue;
    seen.add(key);
    const meta = LINE_COLUMN_META[key];
    const own = form.columnLabels?.[key] ?? meta.formLabel ?? meta.label;
    // A matrix cell reads "NoB" on its own; its group says whose ("As Per DC NoB").
    const group = form.lineGroups?.find(
      (g) =>
        g.columns.includes(key) &&
        g.columns.some((c) => LINE_COLUMN_META[c].label.includes("NoB"))
    );
    missing.push({ key, label: group ? `${group.label} ${own}` : own });
  }
  return missing;
}
